/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {NON_LOT_COMMON_PROPERTY} from '../../common/constants';
import { HttpClient } from '@angular/common/http';
import {LookupService} from '../../common/service/lookup.service';
import {catchError, map, of, throwError} from 'rxjs';
import {equals, hasSubset, isDefinedString, isNumber} from '../../common/service/data-util';
import {AppConfigService} from '../../common/service/app-config.service';
import {NgEventBus} from 'ng-event-bus';
import {PlanModelService} from '../../common/service/plan-model.service';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';

export interface LotRemovalEvent {
    tenureIndex: number;
    lotIndex: number;
}
export interface TenureRemovalEvent {
    tenureIndex: number;
}
@Injectable({
    providedIn: 'root'
})
export class TenureService {
    constructor(
        private eventBus: NgEventBus,
        private http: HttpClient,
        private lookupService: LookupService,
        private appConfigService: AppConfigService,
        private planModelService: PlanModelService
    ) {}

    data = {
        titleCountMap: undefined,
        parentLotResponseMap: undefined,
        titleResponseMap: []
    };

    getModel() {
        return this.planModelService.getModel();
    }

    // DCX-8353 REST API Security (use title status only - do not retrieve full title object))
    getTitleStatus(titleIdentifier) {
        const url = this.appConfigService.settings.apiGatewayUrl + '/document/findTitleStatusByTitleReference/' + titleIdentifier;
        return this.http.get(url, {responseType: 'text'});
    }

    getLot(parentPlanType, parentPlanNumber, parentLotIdentifier, nonLotCommonProperty) {
        let url = '';
        if (nonLotCommonProperty) {
            if (!isDefinedString(parentPlanType)) {
                return throwError(() => new Error('No valid plan type'));
            }
            if (!isNumber(parentPlanNumber)) {
                return throwError(() => new Error('No valid plan number'));
            }
            url =
                this.appConfigService.settings.apiGatewayUrl +
                '/lots/check/' +
                nonLotCommonProperty +
                '/' +
                parentPlanType +
                '/' +
                parentPlanNumber +
                '/custom';
            return this.http.get(url, {transferCache: false});
        } else {
            if (!isDefinedString(parentPlanType)) {
                return throwError(() => new Error('No valid plan type'));
            }
            if (!isDefinedString(parentLotIdentifier)) {
                return throwError(() => new Error('No valid parent lot'));
            }
            if (!isNumber(parentPlanNumber)) {
                return throwError(() => new Error('No valid plan number'));
            }
            url =
                this.appConfigService.settings.apiGatewayUrl +
                '/lots/' +
                parentPlanType +
                '/' +
                parentPlanNumber +
                '/' +
                parentLotIdentifier +
                '/' +
                parentLotIdentifier +
                '/custom';
            return this.http.get(url, {transferCache: false}).pipe(map((response: any[]) => response[0]));
        }
    }

    getCentroid(lotIds) {
        const url = this.appConfigService.settings.apiGatewayUrl + '/polygons/getCentroid';
        return this.http.post(url, lotIds);
    }

    parseRange(textRange) {
        if (textRange && textRange.value) {
            const range = textRange.value.split('-');
            const rangeFrom = parseInt(range[0]);
            if (!isNaN(rangeFrom)) {
                const rangeTo = range.length > 1 && !isNaN(parseInt(range[1])) ? parseInt(range[1]) : rangeFrom;
                if (rangeFrom <= rangeTo) {
                    return {from: rangeFrom, to: rangeTo};
                }
            }
        }
        return undefined;
    }

    rangesToNumbers(ranges) {
        return ranges
            .reduce((acc, range) => {
                for (let n = range.from; n <= range.to; n++) {
                    if (acc.indexOf(n) === -1) {
                        acc.push(n);
                    }
                }
                return acc;
            }, [])
            .sort((a, b) => a - b);
    }

    toRanges(values) {
        const ranges = [];
        let fromValue, toValue;

        for (let i = 0; i < values.length; i++) {
            fromValue = values[i];
            toValue = fromValue;
            while (i + 1 < values.length && values[i + 1] - values[i] === 1) {
                toValue = values[i + 1];
                i++;
            }
            ranges.push({from: fromValue, to: toValue});
        }
        return ranges;
    }

    toTextRange(range: any) {
        return range.to === range.from ? range.from.toString() : range.from + '-' + range.to;
    }

    toTextRanges(ranges: any[]) {
        return ranges.map(range => ({
            value: this.toTextRange(range)
        }));
    }

    numbersToTextRanges(items: any[]) {
        return this.toTextRanges(this.toRanges(items));
    }

    reduceNumbers(numbers) {
        return numbers
            .reduce((acc, v) => {
                if (acc.indexOf(v) === -1) {
                    acc.push(v);
                }
                return acc;
            }, [])
            .sort((a, b) => a - b);
    }

    reduceRanges(ranges) {
        return this.toRanges(this.rangesToNumbers(ranges));
    }

    parseNumbersFromRanges(textRangeList: any[]) {
        return this.rangesToNumbers(textRangeList.map(textRange => this.parseRange(textRange)).filter((range: any) => range));
    }

    parseRanges(rangeTextList: any[]) {
        return this.toRanges(this.parseNumbersFromRanges(rangeTextList));
    }

    sanitiseRangesText(rangesText) {
        const textRanges = rangesText
            .replace(/\s+/g, '')
            .split(',')
            .map(v => ({value: v}));
        return this.toTextRanges(this.parseRanges(textRanges))
            .map(v => v.value)
            .join(', ');
    }

    getLabelMap(formerTenureUILookup) {
        return {
            parentPlanType: formerTenureUILookup.parentTenurePlanTypeLabel,
            parentPlan: formerTenureUILookup.parentTenurePlanNumberLabel,
            parentLot: formerTenureUILookup.parentTenureLotNumberListLabel,
            titleReference: formerTenureUILookup.titleReferenceLabel,
            subjectLandDescription: formerTenureUILookup.subjectLandLabel,
            newLotsLand: formerTenureUILookup.newLotsLandLabel,
            localGovernment: formerTenureUILookup.localGovtLabel,
            locality: formerTenureUILookup.localityLabel
        };
    }

    getParentLotResponse(parentPlanTypeCode, parentPlanNumber, parentLotIdentifier) {
        return this.data.parentLotResponseMap[parentPlanTypeCode + parentPlanNumber + '_' + parentLotIdentifier];
    }

    getNonLotCommonPropertyResponse(parentPlanTypeCode, parentPlanNumber) {
        return this.data.parentLotResponseMap[parentPlanTypeCode + parentPlanNumber + '_COMMONPROPERTY'];
    }

    getTitleResponse(titleIdentifier) {
        return this.data.titleResponseMap[titleIdentifier];
    }

    formatRanges(ranges) {
        return this.toTextRanges(ranges)
            .map(textRange => textRange.value)
            .join(', ');
    }

    getTenureCardinality(model) {
        return model.formerTenures.reduce((acc, tenure) => acc + Math.max(tenure.lots.length, 1), 0);
    }

    isTitleDuplicated(title) {
        const titleCount = (this.data.titleCountMap && this.data.titleCountMap[title]) || 0;
        return titleCount > 1;
    }

    validate(viewModel) {
        const model = this.toModel(viewModel);
        this.data.titleCountMap = model.formerTenures
            .filter(v => v.title)
            .reduce((acc, v) => {
                if (!acc[v.title]) {
                    acc[v.title] = 1;
                } else {
                    acc[v.title]++;
                }
                return acc;
            }, {});
    }

    verify(viewModel) {
        this.data.parentLotResponseMap = this.data.parentLotResponseMap || {};

        const model = this.toModel(viewModel);

        const duplicateMap = model.formerTenures.reduce((acc, v) => {
            const nonLotCommonPropertyList = v.lots.filter(l => l.nonLotCommonProperty);
            if (nonLotCommonPropertyList.length === 0) {
                return acc;
            }
            const k = `${v.parentPlanType.id}${v.parentPlan}`;
            if (!acc[k]) {
                acc[k] = [];
            }
            acc[k].push(...nonLotCommonPropertyList);
            return acc;
        }, {});

        model.formerTenures
            .filter(v => v.parentPlanType && v.parentPlan)
            .forEach(v => {
                const k = `${v.parentPlanType.id}${v.parentPlan}`;
                const parentLotResponse = {
                    lot: undefined,
                    error: undefined
                };

                if (duplicateMap[k] && duplicateMap[k].length > 1) {
                    parentLotResponse.error = 'Same plan cannot have more than one common property lot.';
                } else {
                    parentLotResponse.lot = 'COMMONPROPERTY';
                }
                this.data.parentLotResponseMap[`${k}_COMMONPROPERTY`] = parentLotResponse;
            });

        model.formerTenures.forEach(v => {
            if (v.title) {
                const subs = this.getTitleStatus(v.title).subscribe({
                    next: data => {
                        const titleStatus = data;
                        if (titleStatus === 'CURRENT') {
                            this.data.titleResponseMap[v.title] = {title: titleStatus};
                        } else {
                            this.data.titleResponseMap[v.title] = {error: 'Title is not current'};
                        }
                    },
                    error: err => {
                        console.log('Error getting title' + err);
                        this.data.titleResponseMap[v.title] = {error: 'Error getting title'};
                    }
                });
                this.eventBus.cast('nlr:busy', subs);
            }
            if (v.lots.length) {
                this.processAllLots(v, duplicateMap);
            }
        });

        return model;
    }
    processAllLots(v, duplicateMap): void {
        const httpCalls = v.lots.map(lot =>
            this.getLot(v.parentPlanType.id, v.parentPlan, `${lot.parentLotPrefix || ''}${lot.parentLot || ''}`, lot.nonLotCommonProperty)
        );
        // Wrapping each HTTP call in catchError to handle individual errors
        const httpCallsWithErrorHandling = httpCalls.map(call => call.pipe(catchError(error => of({error: true, details: error}))));

        const subscription = forkJoinDefaultIfEmpty(httpCallsWithErrorHandling).subscribe((responses: any[]) => {
            this.data.parentLotResponseMap = responses.reduce((acc, response, responseIdx) => {
                const parentLot = v.lots[responseIdx];
                let parentLotResponseMapKey = '';
                if (parentLot.nonLotCommonProperty) {
                    parentLotResponseMapKey = v.parentPlanType.id + v.parentPlan + '_COMMONPROPERTY';
                } else {
                    parentLotResponseMapKey = v.parentPlanType.id + v.parentPlan + '_' + parentLot.parentLotPrefix + parentLot.parentLot;
                }

                const parentLotResponse = {
                    error: undefined,
                    response: undefined,
                    lot: undefined
                };

                const legacySurveyStageStatuses = this.lookupService.getLookups('surveyStageStatuses').filter(sss => {
                    return sss.legacyLegalStatus !== null;
                });
                const legacyLandStatuses = this.lookupService.getLookups('landStatuses').filter(ls => {
                    return ls.legacyLotStatus !== null;
                });

                const k = `${v.parentPlanType.id}${v.parentPlan}`;
                if (!duplicateMap[k] || duplicateMap[k].length === 1) {
                    if (response?.details?.error) {
                        parentLotResponse.error = response.details.error;
                    } else if (response === true) {
                        parentLotResponse.response = response;
                    } else if (legacyLandStatuses && !legacySurveyStageStatuses.some(sss => sss.legacyLegalStatus.includes(response.legalStatus))) {
                        parentLotResponse.error = 'Plan is invalid';
                    } else if (legacySurveyStageStatuses && !legacyLandStatuses.some(ls => ls.legacyLotStatus.includes(response.lotStatus))) {
                        parentLotResponse.error = 'Lot is invalid';
                    } else {
                        parentLotResponse.lot = response;
                        parentLot.lotId = parentLotResponse.lot.lotId;
                        parentLot.lga = parentLotResponse.lot.lga;
                        parentLot.locality = parentLotResponse.lot.locality;
                        parentLot.titles = parentLotResponse.lot.titles;

                        parentLot.parentLotType = parentLotResponse.lot.lotType;
                    }

                    acc[parentLotResponseMapKey] = parentLotResponse;
                }
                return acc;
            }, this.data.parentLotResponseMap);
        });
        this.eventBus.cast('nlr:busy', subscription);
    }

    //TODO Extract modal call
    editTenures(tenureDisplayType, model, lookups) {
        this.data.parentLotResponseMap = this.data.parentLotResponseMap || {};

        let filteredParentPlanTypeLookups = [];
        let filteredSubjectLandTypeLookups = [];

        if (lookups.formerTenureValidityLookup) {
            const parentPlanTypes = lookups.formerTenureValidityLookup.parentPlanType;

            filteredParentPlanTypeLookups = lookups.parentPlanTypeLookups.filter(v => parentPlanTypes && parentPlanTypes.indexOf(v.code) > -1);

            const subjectLandTypes = lookups.formerTenureValidityLookup.subjectLandTypes;

            if (subjectLandTypes) {
                filteredSubjectLandTypeLookups = lookups.subjectLandTypeLookups.filter(
                    v => v.active && parentPlanTypes && subjectLandTypes.indexOf(v.id) > -1
                );
            }
        }

        // let modalInstance = $uibModal.open({
        //   templateUrl: '/components/plan-information/tenure/tenure-modal.html',
        //   controller: 'TenureModalController',
        //   controllerAs: '$ctrl',
        //   windowTemplateUrl: '/components/modal/modal.html',
        //   size: 'xlg',
        //   backdrop: 'static',
        //   keyboard: false,
        //   animation: false,
        //   resolve: {
        //     modalData: function() {
        //       return {
        //         tenureDisplayType: tenureDisplayType,
        //         lookups: Object.assign({}, lookups, {
        //           parentPlanTypeLookups: filteredParentPlanTypeLookups,
        //           subjectLandTypeLookups: filteredSubjectLandTypeLookups
        //         }),
        //         model: angular.copy(model)
        //       };
        //     }
        //   }
        // });
        //
        // return modalInstance.result;
    }

    toSummaryViewModel(model, parentPlanTypeLookups, subjectLandTypeLookups, formerTenureUiConfigLookup) {
        const fullySupersededByRoadText = 'ROAD / ROAD WIDENING';

        return {
            tenures: model.formerTenures.map(tenure => {
                const tenureSummary = JSON.parse(JSON.stringify(tenure));
                // new lot details
                if (tenure.newLotDetails) {
                    tenureSummary.newLotDetails = tenure.newLotDetails.fullySupersededByRoad
                        ? fullySupersededByRoadText
                        : this.formatRanges(this.toRanges(tenure.newLotDetails.newLots));
                } else {
                    tenureSummary.newLotDetails = '';
                }

                // parent plan type - not set on NLOP former tenures
                if (tenure.parentPlanType) {
                    tenureSummary.parentPlanType = parentPlanTypeLookups.filter(parentPlanTypeLookup => {
                        return parentPlanTypeLookup.id === tenure.parentPlanType.id;
                    })[0].name;

                    // lots
                    tenureSummary.lots.forEach(lot => {
                        if (lot.nonLotCommonProperty) {
                            lot.parentLotIdentifier = NON_LOT_COMMON_PROPERTY;
                            lot.newLotDetails = '';
                            lot.titles = '';
                            return;
                        }

                        lot.parentLotIdentifier =
                            `${lot.parentLotPrefix || ''}${lot.parentLot || ''}` +
                            (['PAW', 'ROW', 'VEST', 'CP'].indexOf(lot.parentLotType) !== -1 ? ' (' + lot.parentLotType + ')' : '');

                        if (lot.newLotDetails) {
                            lot.newLotDetails = lot.newLotDetails.fullySupersededByRoad
                                ? fullySupersededByRoadText
                                : this.formatRanges(this.toRanges(lot.newLotDetails.newLots));
                        } else {
                            lot.newLotDetails = '';
                        }

                        lot.titles = lot.titles ? this.sortTitleIdentifiers(lot.titles).join(', ') : '';
                    });
                } else {
                    // default to parentPlanTypeSubjectLandOption text configured in the FormerTenureUiConfigLookup
                    tenureSummary.parentPlanType = formerTenureUiConfigLookup.parentPlanTypeSubjectLandOption;
                }

                // subject land type
                if (tenure.subjectLandType) {
                    tenureSummary.subjectLandDescription = [
                        subjectLandTypeLookups.filter(subjectLandTypeLookup => subjectLandTypeLookup.id === tenure.subjectLandType.id)[0].description,
                        tenure.subjectLandDescription || ''
                    ]
                        .join(' / ')
                        .toUpperCase();
                }
                return tenureSummary;
            })
        };
    }

    toDetailViewModel(model) {
        return this.fromModel(model);
    }

    sortAndJoinTitleIdentifiers(titleIdentifiers: string[] | string) {
        return Array.isArray(titleIdentifiers) ? (titleIdentifiers as string[]).sort((t1, t2) => t1.localeCompare(t2)).join(', ') : titleIdentifiers;
    }
    sortTitleIdentifiers(titleIdentifiers: string[] | string) {
        return Array.isArray(titleIdentifiers) ? (titleIdentifiers as string[]).sort((t1, t2) => t1.localeCompare(t2)) : [titleIdentifiers];
    }

    getLotPrefix(lotIdentifier) {
        if (typeof lotIdentifier === 'string') {
            const lotPrefixMatch = lotIdentifier.match(/^[A-Z]{0,2}/);
            if (lotPrefixMatch) {
                return lotPrefixMatch[0];
            }
        }
        return '';
    }

    getLotNumber(lotIdentifier) {
        if (typeof lotIdentifier === 'number') {
            return lotIdentifier;
        } else if (typeof lotIdentifier === 'string') {
            const lotNumberMatch = lotIdentifier.match(/\d{1,6}$/);
            if (lotNumberMatch) {
                return parseInt(lotNumberMatch[0]);
            }
        }
        return -1;
    }

    sortLotIdentifiers(lotIdentifiers) {
        return lotIdentifiers.sort((lotIdentifier1, lotIdentifier2) => {
            const lotPrefix1 = this.getLotPrefix(lotIdentifier1);
            const lotNumber1 = this.getLotNumber(lotIdentifier1);
            const lotPrefix2 = this.getLotPrefix(lotIdentifier2);
            const lotNumber2 = this.getLotNumber(lotIdentifier2);

            if (lotPrefix1 === lotPrefix2) {
                return lotNumber1 - lotNumber2;
            }

            return lotPrefix1.localeCompare(lotPrefix2);
        });
    }

    fromLots(lots) {
        if (!lots) {
            return [];
        }
        return lots.map(lot => {
            const lotViewModel = structuredClone(lot);
            if (lot.parentLotPrefix || lot.parentLot) {
                lotViewModel.parentLotIdentifier = `${lot.parentLotPrefix || ''}${lot.parentLot || ''}`;
                if (Object.hasOwn(lotViewModel, 'parentLotPrefix')) {
                    delete lotViewModel.parentLotPrefix;
                }
                if (Object.hasOwn(lotViewModel, 'parentLot')) {
                    delete lotViewModel.parentLot;
                }
            }

            lotViewModel.newLotDetails = this.fromNewLotDetails(lot.newLotDetails);
            return lotViewModel;
        });
    }
    toLots(lotViewModels) {
        if (!lotViewModels) {
            return [];
        }

        return lotViewModels.map(lotViewModel => {
            const lot = structuredClone(lotViewModel);
            if (lot.parentLotIdentifier) {
                if (lotViewModel.nonLotCommonProperty) {
                    lot.parentLotPrefix = '';
                    lot.parentLot = '';
                } else {
                    const lotPrefixMatch = lot.parentLotIdentifier.match(/^[A-Z]{0,2}/);
                    lot.parentLotPrefix = lotPrefixMatch ? lotPrefixMatch[0] : '';
                    const lotNumberMatch = lot.parentLotIdentifier.match(/\d{1,6}$/);
                    lot.parentLot = lotNumberMatch ? lotNumberMatch[0] : '';
                }

                delete lot.parentLotIdentifier;
            }
            lot.newLotDetails = this.toNewLotDetails(lotViewModel.newLotDetails);
            lot.nonLotCommonProperty = lotViewModel.nonLotCommonProperty;
            return lot;
        });
    }

    fromNewLotDetails(newLotDetails) {
        return {
            fullySupersededByRoad: newLotDetails && newLotDetails.fullySupersededByRoad,
            newLotRanges: newLotDetails && newLotDetails.newLots ? this.numbersToTextRanges(newLotDetails.newLots) : []
        };
    }

    toNewLotDetails(newLotDetails) {
        return {
            fullySupersededByRoad: newLotDetails && newLotDetails.fullySupersededByRoad,
            newLots: newLotDetails && newLotDetails.newLotRanges ? this.parseNumbersFromRanges(newLotDetails.newLotRanges) : []
        };
    }

    toTenure(tenureViewModel) {
        const parentLotToNewLotMap = tenureViewModel.lots
            .filter(lotViewModel => lotViewModel && (lotViewModel.parentLotIdentifier || lotViewModel.nonLotCommonProperty))
            .reduce((acc, lotViewModel) => {
                const newLots =
                    lotViewModel.newLotDetails && lotViewModel.newLotDetails.newLotRanges
                        ? this.rangesToNumbers(this.parseRanges(lotViewModel.newLotDetails.newLotRanges))
                        : [];
                if (acc[lotViewModel.parentLotIdentifier]) {
                    const filteredNewLotNumbers = newLots.filter(v => acc[lotViewModel.parentLotIdentifier].newLotDetails.newLots.indexOf(v) === -1);

                    Array.prototype.push.apply(acc[lotViewModel.parentLotIdentifier].newLotDetails.newLots, filteredNewLotNumbers);
                    acc[lotViewModel.parentLotIdentifier].newLotDetails.newLots.sort((a, b) => a - b);
                } else {
                    acc[lotViewModel.parentLotIdentifier] = {
                        nonLotCommonProperty: lotViewModel.nonLotCommonProperty,
                        newLotDetails: {
                            fullySupersededByRoad: lotViewModel.newLotDetails && lotViewModel.newLotDetails.fullySupersededByRoad,
                            newLots: newLots
                        }
                    };
                }

                return acc;
            }, {});

        const parentLotGroups = this.sortLotIdentifiers(Object.keys(parentLotToNewLotMap)).reduce((acc, parentLotIdentifier) => {
            const parentLotToNewLotMapItem = parentLotToNewLotMap[parentLotIdentifier];
            const newLotDetails = parentLotToNewLotMapItem.newLotDetails;
            const nonLotCommonProperty = parentLotToNewLotMapItem.nonLotCommonProperty;

            const matchingGroup = acc.filter(
                group => group.newLotDetails === newLotDetails && group.nonLotCommonProperty === nonLotCommonProperty
            )[0];

            if (matchingGroup) {
                matchingGroup.parentLotIdentifiers.push(parentLotIdentifier);
            } else {
                acc.push({
                    parentLotIdentifiers: nonLotCommonProperty ? [''] : [parentLotIdentifier],
                    newLotDetails: newLotDetails,
                    nonLotCommonProperty: nonLotCommonProperty
                });
            }

            return acc;
        }, []);

        const parentLots = parentLotGroups.reduce((acc, parentLotGroup) => {
            const fullySupersededByRoad = parentLotGroup.newLotDetails.fullySupersededByRoad;
            const groupNewLotRanges = this.numbersToTextRanges(parentLotGroup.newLotDetails.newLots);
            const nonLotCommonProperty = parentLotGroup.nonLotCommonProperty;

            return acc.concat(
                parentLotGroup.parentLotIdentifiers.map(parentLotIdentifier => {
                    return {
                        parentLotIdentifier: parentLotIdentifier,
                        newLotDetails: {
                            fullySupersededByRoad: fullySupersededByRoad,
                            newLotRanges: groupNewLotRanges
                        },
                        nonLotCommonProperty: nonLotCommonProperty
                    };
                })
            );
        }, []);

        const tenure = structuredClone(tenureViewModel);

        if (tenure.parentPlanType) {
            tenure.parentPlanType = {
                id: tenureViewModel.parentPlanType,
                code: tenureViewModel.parentPlanType
            };
        }
        if (tenure.subjectLandType) {
            tenure.subjectLandType = tenureViewModel.subjectLandType;
        }

        const newLots = this.toLots(parentLots);

        //If new lot is a subset of an existing lot, retain the existing lot, otherwise replace the existing lot with the new lot
        tenure.lots = newLots.map(newLot => tenure.lots.find(lot => hasSubset(lot, newLot)) || newLot);

        tenure.newLotDetails = this.toNewLotDetails(tenureViewModel.newLotDetails);

        return tenure;
    }
    toTenures(tenureViewModels) {
        return tenureViewModels
            .map(v => this.toTenure(v))
            .reduce((acc, tenure) => {
                const tenureWithDuplicateParentPlan = acc.filter(
                    v =>
                        (v.parentPlanType ? v.parentPlanType.id : '') === (tenure.parentPlanType ? tenure.parentPlanType.id : '') &&
                        (v.parentPlan || null) === (tenure.parentPlan || null) &&
                        (v.subjectLandType || null) === (tenure.subjectLandType || null) &&
                        (v.subjectLandDescription || null) === (tenure.subjectLandDescription || null) &&
                        (v.title || null) === (tenure.title || null)
                )[0];
                if (tenureWithDuplicateParentPlan) {
                    const parentLots = tenure.lots.reduce((accLots, lot) => {
                        const duplicateLot = accLots.filter(accLot => {
                            return (
                                accLot.parentLotPrefix === lot.parentLotPrefix &&
                                accLot.parentLotType === lot.parentLotType &&
                                accLot.parentLot === lot.parentLot
                            );
                        })[0];
                        if (duplicateLot) {
                            if (lot.newLotDetails.newLots.length) {
                                duplicateLot.newLotDetails.fullySupersededByRoad = false;
                                const newLots = this.reduceNumbers(duplicateLot.newLotDetails.newLots.concat(lot.newLotDetails.newLots));
                                duplicateLot.newLotDetails.newLots.length = 0;
                                Array.prototype.push.apply(duplicateLot.newLotDetails.newLots, newLots);
                            }
                        } else {
                            accLots.push(lot);
                        }
                        return accLots;
                    }, tenureWithDuplicateParentPlan.lots.slice());

                    tenureWithDuplicateParentPlan.lots.length = 0;
                    Array.prototype.push.apply(tenureWithDuplicateParentPlan.lots, parentLots);
                } else {
                    acc.push(tenure);
                }
                return acc;
            }, []);
    }
    fromTenures(tenures) {
        return tenures.map(tenure => {
            const tenureViewModel = structuredClone(tenure);
            tenureViewModel.parentPlanType = tenure.parentPlanType ? tenure.parentPlanType.id : null;
            tenureViewModel.subjectLandType = tenure.subjectLandType ? tenure.subjectLandType : null;
            tenureViewModel.lots = this.fromLots(tenure.lots);
            tenureViewModel.newLotDetails = this.fromNewLotDetails(tenure.newLotDetails);
            return tenureViewModel;
        });
    }
    fromModel(model) {
        return {
            planNumber: model.planNumber,
            tenures: this.fromTenures(model.formerTenures)
        };
    }

    toModel(viewModel) {
        return {
            formerTenures: this.toTenures(viewModel.tenures)
        };
    }

    updateInterests() {
        const planModel = this.getModel();
        if (planModel.interests) {
            planModel.interests
                .filter(interest => interest.burdenedAffectedTenures.length)
                .forEach(interest =>
                    interest.burdenedAffectedTenures.splice(
                        0,
                        interest.burdenedAffectedTenures.length,
                        ...interest.burdenedAffectedTenures.filter(
                            bat =>
                                planModel.formerTenures.includes(bat.affectedTenure) &&
                                (!bat.affectedTenureLot || bat.affectedTenure.lots.includes(bat.affectedTenureLot))
                        )
                    )
                );
        }
    }

    formerTenureChanged(oldValue, newValue) {
        if (equals(newValue, oldValue)) {
            return false;
        }

        const planModel = this.getModel();

        if (!planModel.formerTenures) {
            return false;
        }

        // Localities and LGAs are either derived from tenures, or user defined. To ensure user defined localities and LGAs are retained,
        // only remove localities and LGAs derived from old tenures that cannot be derived from new tenures.

        const oldTenureLots = oldValue
            ? oldValue.reduce((acc, tenure) => {
                  acc.push(...tenure.lots);
                  return acc;
              }, [])
            : [];
        const oldTenureLocalities = oldTenureLots
            .map(lot => lot.locality)
            .filter(v => v)
            .filter((v, i, a) => i === a.indexOf(v));
        const oldTenureLgas = oldTenureLots
            .map(lot => lot.lga)
            .filter(v => v)
            .filter((v, i, a) => i === a.indexOf(v));

        const newTenureLots = newValue.reduce((acc, tenure) => {
            acc.push(...tenure.lots);
            return acc;
        }, []);
        const newTenureLocalities = newTenureLots
            .map(lot => lot.locality)
            .filter(v => v)
            .filter((v, i, a) => i === a.indexOf(v));
        const newTenureLgas = newTenureLots
            .map(lot => lot.lga)
            .filter(v => v)
            .filter((v, i, a) => i === a.indexOf(v));

        const localitiesToRemove = oldTenureLocalities.filter(v => !newTenureLocalities.includes(v));
        const lgasToRemove = oldTenureLgas.filter(v => !newTenureLgas.includes(v));

        planModel.localities = planModel.localities || [];
        planModel.localities.splice(
            0,
            planModel.localities.length,
            ...planModel.localities.filter(v => v.localityName && !localitiesToRemove.includes(v.localityName))
        );
        planModel.localities.push(
            ...newTenureLocalities.filter(v => planModel.localities.every(locality => locality.localityName !== v)).map(v => ({localityName: v}))
        );

        planModel.localGovernmentAuthorities = planModel.localGovernmentAuthorities || [];
        planModel.localGovernmentAuthorities.splice(
            0,
            planModel.localGovernmentAuthorities.length,
            ...planModel.localGovernmentAuthorities.filter(v => v.lgaName && !lgasToRemove.includes(v.lgaName))
        );
        planModel.localGovernmentAuthorities.push(
            ...newTenureLgas.filter(v => planModel.localGovernmentAuthorities.every(lga => lga.lgaName !== v)).map(v => ({lgaName: v}))
        );

        if (planModel.localGovernmentAuthorities.length === 0) {
            planModel.localGovernmentAuthorities.push({lgaName: ''});
        }

        if (planModel.localities.length === 0) {
            planModel.localities.push({localityName: ''});
        }

        this.updateInterests();

        return true;

        this.eventBus.cast('nlr:plan-model-updated:formerTenures', '');
    }
}
